import { useEffect, useRef, useState } from "react";

/**
 * SSR-safe scroll reveal state.
 *
 * The first render (server HTML + first client paint) is always VISIBLE, so
 * content is never blank for crawlers, no-JS visitors, screenshots, or slow
 * devices. After hydration we only hide elements that are still below the
 * viewport, then fade them in when they scroll into view.
 */
export function useRevealVisible<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const rect = el.getBoundingClientRect();
    const belowFold = rect.top > window.innerHeight * 0.9;
    if (!belowFold) return;

    // Only elements the visitor has not reached yet get the entrance animation.
    setVisible(false);

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return { ref, visible };
}
